import {
  type Cell,
  type ColumnDef,
  type Row,
  type SortingState,
  type Table,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Search } from "lucide-react";
import * as React from "react";

import { Button } from "./button";
import { Input } from "./input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Skeleton } from "./skeleton";
import { EditableCell } from "./editable-cell";
import { formatNumber } from "@/lib/format";
import { topChromeOffset } from "@/lib/top-chrome";
import { cn } from "@/lib/utils";

export type DataTableColumn<TData, TValue = unknown> = ColumnDef<TData, TValue>;

/**
 * Render a cell, wrapped in an in-place editor when its column declares
 * `meta.edit` and the table was given an `onCellEdit` handler (AB#7874).
 * Every cell path in this file routes through here so the desktop grid and
 * the mobile card layout stay in step.
 */
function renderCell<TData>(
  cell: Cell<TData, unknown>,
  onCellEdit?: (row: TData, field: string, value: string | null) => Promise<void>,
) {
  const content = flexRender(cell.column.columnDef.cell, cell.getContext());
  const edit = cell.column.columnDef.meta?.edit;
  if (!edit || !onCellEdit) return content;
  return (
    <EditableCell row={cell.row.original} meta={edit} onSave={onCellEdit}>
      {content}
    </EditableCell>
  );
}

/** "all" renders every row on one page (no pagination). */
export type PageSizeOption = number | "all";

/**
 * Opt-in server-driven pagination. When a page passes this, the table renders
 * exactly the `data` rows it's handed (one server page — no client slicing or
 * filtering), and the footer's result count, page controls, and rows-per-page
 * selector are driven by these values, reporting changes back so the caller
 * re-fetches. Search stays page-owned in this mode (a server-paginated page
 * drives the `q` param itself) — omit `searchPlaceholder`. Sorting acts on the
 * loaded page only UNLESS `sort` + `onSortChange` are supplied, which switches
 * the table to server-driven sort (header clicks re-fetch the whole dataset).
 */
export interface ServerPaginationConfig {
  /** 1-based current page (matches the API's `page`). */
  page: number;
  /** Current page size (`per_page`). */
  perPage: number;
  /** Total matching rows across all pages — drives the footer "N results". */
  totalRows: number;
  /** Total page count (`total_pages`) — drives prev/next enablement. */
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Rows-per-page options. Defaults to [25, 50, 100, 200]. */
  perPageOptions?: number[];
  onPerPageChange: (perPage: number) => void;
  /** Current server sort as TanStack `SortingState` (single column). Omit for
   *  the server's default order. */
  sort?: SortingState;
  /** Called on a sortable-header click. When provided, the table sorts
   *  server-side (`manualSorting`) — the page maps the column to a sort field
   *  and re-fetches — instead of reordering just the loaded page. */
  onSortChange?: (next: SortingState) => void;
}

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /**
   * Rendered when no rows match. Accepts either a plain string (shown as
   * centered muted text) or a ReactNode for richer cases — typically an
   * `<EmptyState>` with an icon and a CTA button.
   */
  emptyMessage?: React.ReactNode;
  isLoading?: boolean;
  /** Skeleton rows to render while loading. Defaults to 5. */
  loadingRows?: number;
  /** Search input placeholder. Omit to hide the search box entirely. */
  searchPlaceholder?: string;
  /** Optional slot rendered to the right of the search input (e.g. a "New" button). */
  toolbarActions?: React.ReactNode;
  /** Called when a row is clicked. When omitted, rows are not interactive. */
  onRowClick?: (row: TData) => void;
  /**
   * Enables in-cell editing (AB#7874). A column opts in by declaring
   * `meta.edit` (a `ColumnEditMeta`); this callback commits one field.
   * Resolve to apply, reject to leave the cell as it was. Without it, every
   * column stays read-only however its meta is declared.
   */
  onCellEdit?: (row: TData, field: string, value: string | null) => Promise<void>;
  /**
   * Rows per page on first render. Defaults to 25. Pass `"all"` for a table
   * whose readers want the whole set in front of them — pair it with an
   * `"all"` entry in `pageSizeOptions` so they can page down again.
   */
  pageSize?: PageSizeOption;
  /**
   * Render a "Rows per page" selector in the footer. When omitted, the page
   * size stays fixed at `pageSize`. Pass `"all"` to include an "All" option
   * that renders every row on one page.
   */
  pageSizeOptions?: PageSizeOption[];
  /**
   * Fires when the user picks a different option from the page-size selector.
   * Pages that drive their server query off pageSize (i.e. want the API to
   * return that many rows, not just paginate client-side) should re-issue the
   * fetch here. Receives the user-facing value (`number` or `"all"`).
   */
  onPageSizeChange?: (next: PageSizeOption) => void;
  /** Key accessor used for React list keys. Defaults to `(row) => JSON.stringify(row)` which is fine for small datasets. */
  getRowId?: (row: TData) => string;
  /**
   * Initial sort state when the table first renders. Takes the same shape as
   * TanStack's `SortingState`. Users can still click headers to override.
   * Example: `[{ id: "name", desc: false }]` for alphabetical by name.
   */
  defaultSort?: SortingState;
  /** Custom class on the outer card. */
  className?: string;
  /**
   * On phones (<640px) each row collapses into a stacked label:value card by
   * default — the same TanStack cells, re-laid-out vertically so wide tables
   * don't force horizontal scrolling on a small screen. Tablets and up keep
   * the classic table. Set this to keep the horizontal-scroll table on phones
   * too (e.g. a table whose cells only make sense in a grid).
   */
  disableMobileCards?: boolean;
  /**
   * Freeze the header row so it stays visible while the PAGE scrolls
   * vertically (Excel-style), while the grid still scrolls horizontally and
   * the page keeps its normal vertical scroll + outside pager. Implemented as
   * two scroll-synced tables (header + body) with a shared `colgroup` so the
   * columns line up exactly; the card uses `overflow: clip` so the sticky
   * header escapes to the page instead of being captured by the card. Opt-in
   * because it needs every column to carry a sensible `size`.
   */
  frozenHeader?: boolean;
  /**
   * Distance from the top of the viewport at which the frozen header sticks,
   * in px — set this to the height of any fixed/sticky chrome above the table
   * (e.g. a sticky topbar). Defaults to the bottom of the app shell's chrome
   * (`topChromeOffset()`): the page scrolls under that sticky bar, so a header
   * stuck at 0 would sit behind it, out of sight.
   */
  frozenHeaderTop?: number;
  /**
   * Hold every column at its declared `size` at the least. By default the
   * browser squeezes columns below their sizes to fit the card, so a wide
   * grid wraps its headers and truncates its values instead of scrolling.
   * With this set, a grid wider than the card scrolls horizontally, with the
   * top and bottom scroll bars. Opt-in, like `frozenHeader`, because it needs
   * every column to carry a sensible `size`.
   */
  keepColumnWidths?: boolean;
  /**
   * Per-column pixel widths, keyed by column id. Supplying this together with
   * `onColumnSizingChange` turns on drag-to-resize handles in the header and
   * makes sizing controlled; omitting both leaves the table at its columns'
   * declared `size`. The caller owns the map so it can be persisted (saved
   * view + localStorage snapshot).
   */
  columnSizing?: Record<string, number>;
  /** Called on every drag frame with the full width map. */
  onColumnSizingChange?: (next: Record<string, number>) => void;
  /**
   * Opt into server-driven pagination. Omit for the default client-side
   * behavior (filter + paginate the full `data` array in the browser). See
   * {@link ServerPaginationConfig}.
   */
  server?: ServerPaginationConfig;
}

const ALL_ROWS_SIZE = 100_000;
const DEFAULT_SERVER_PAGE_SIZES = [25, 50, 100, 200];

/**
 * Token-themed TanStack Table v8 wrapper. Reach for this whenever you need a
 * data grid. Ships with:
 *
 *   · Column sorting (click headers)
 *   · Global search filter (fuzzy match across all accessors)
 *   · Pagination + optional rows-per-page selector
 *   · Self-contained 2D scroll region (bounded height) with a sticky header
 *     that stays visible while scrolling rows, and horizontal scroll so wide
 *     tables reach every column
 *   · Sticky top scrollbar mirroring the table's scrollLeft, so wide tables
 *     can be panned from the top without reaching the wrapper's bottom bar
 *   · Empty / loading states
 *   · Optional toolbar action slot for "New" buttons etc.
 *   · Clickable rows via onRowClick
 *   · Mobile (<640px): rows collapse into stacked label:value cards so wide
 *     tables stay usable on phones (opt out with `disableMobileCards`)
 *
 * Keep page-specific logic (formatting, custom cells) in the `columns` prop;
 * this component should never grow knowledge of any domain entity.
 */
export function DataTable<TData>({
  columns,
  data,
  emptyMessage = "No results.",
  isLoading = false,
  loadingRows = 5,
  searchPlaceholder,
  toolbarActions,
  onRowClick,
  onCellEdit,
  pageSize = 25,
  pageSizeOptions,
  onPageSizeChange,
  getRowId,
  defaultSort,
  className,
  disableMobileCards = false,
  frozenHeader = false,
  frozenHeaderTop = topChromeOffset(),
  keepColumnWidths = false,
  columnSizing,
  onColumnSizingChange,
  server,
}: DataTableProps<TData>) {
  const serverMode = !!server;
  // Resizing is opt-in: a caller that supplies a width map AND a setter gets
  // drag handles and controlled sizing; everyone else keeps declared sizes.
  const resizable = !!columnSizing && !!onColumnSizingChange;
  // Server-driven sort is opt-in: a server page that supplies `onSortChange`
  // controls the sort externally (header clicks re-fetch); otherwise sorting
  // stays internal (client reorder of the loaded page).
  const sortServer = serverMode && !!server?.onSortChange;
  const [sorting, setSorting] = React.useState<SortingState>(defaultSort ?? []);
  const activeSorting = sortServer ? (server?.sort ?? []) : sorting;
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [pageSizeState, setPageSizeState] = React.useState<PageSizeOption>(pageSize);

  const effectivePageSize =
    pageSizeState === "all" ? ALL_ROWS_SIZE : pageSizeState;

  const table = useReactTable({
    data,
    columns,
    state: serverMode
      ? { sorting: activeSorting, ...(resizable ? { columnSizing } : {}) }
      : {
          sorting: activeSorting,
          globalFilter,
          ...(resizable ? { columnSizing } : {}),
        },
    enableColumnResizing: resizable,
    columnResizeMode: "onChange",
    onColumnSizingChange: resizable
      ? (updater) => {
          const next =
            typeof updater === "function" ? updater(columnSizing) : updater;
          onColumnSizingChange(next);
        }
      : undefined,
    onSortingChange: sortServer
      ? (updater) => {
          const next =
            typeof updater === "function"
              ? updater(server?.sort ?? [])
              : updater;
          server?.onSortChange?.(next);
        }
      : setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Server mode renders exactly the rows it's handed: no client filter/
    // paginate row models, and the manual flags stop TanStack from slicing
    // the page down to its own pageSize. `manualSorting` (server-sort mode)
    // also stops it from re-ordering the loaded page — the server already did.
    ...(server
      ? {
          manualPagination: true,
          manualFiltering: true,
          manualSorting: sortServer,
          pageCount: server.totalPages,
        }
      : {
          onGlobalFilterChange: setGlobalFilter,
          getFilteredRowModel: getFilteredRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
          initialState: { pagination: { pageSize: effectivePageSize } },
        }),
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
  });

  // Push pageSize changes into the table and reset to page 1. The user
  // sitting on page 5 of 50 who switches to 100 expects to land at the top.
  // (Client mode only — server mode drives page size through the API.)
  React.useEffect(() => {
    if (serverMode) return;
    table.setPageSize(effectivePageSize);
    table.setPageIndex(0);
  }, [serverMode, effectivePageSize, table]);

  const rows = table.getRowModel().rows;
  const clientPageCount = table.getPageCount();
  const showPagination = server
    ? server.totalPages > 1
    : clientPageCount > 1;
  // Server mode always shows the footer (result count + rows-per-page).
  const showFooter = showPagination || !!pageSizeOptions || serverMode;

  // Footer values — server-driven when in server mode, else off the table.
  const resultCount = server
    ? server.totalRows
    : table.getFilteredRowModel().rows.length;
  const currentPage = server
    ? server.page
    : table.getState().pagination.pageIndex + 1;
  const totalPages = server ? server.totalPages : clientPageCount;
  const canPrev = server ? server.page > 1 : table.getCanPreviousPage();
  const canNext = server
    ? server.page < server.totalPages
    : table.getCanNextPage();
  const goPrev = server
    ? () => server.onPageChange(server.page - 1)
    : () => table.previousPage();
  const goNext = server
    ? () => server.onPageChange(server.page + 1)
    : () => table.nextPage();

  // Top scrollbar mirror — pinned to the top of `<main>` so users can
  // pan a wide table horizontally without first having to scroll all the
  // way down to the bottom-of-table scrollbar. The scroll container that
  // actually moves is `<main>` (page-level horizontal scroll); the mirror
  // is a thin sticky strip that sits inside the DataTable wrapper but
  // sticks to `top: 0` of the scroll ancestor, staying visible while
  // the user scans rows below.
  const tableScrollRef = React.useRef<HTMLDivElement>(null);
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  // The actual horizontal scroll container — discovered at mount by
  // walking up from the table. The inner table-wrapper isn't a scroll
  // container (sticky `thead` can't survive an intermediate scroll
  // ancestor), so horizontal panning lives on `<main>`. We capture that
  // ancestor and drive its `scrollLeft` from the top mirror.
  const scrollAncestorRef = React.useRef<HTMLElement | null>(null);
  const [tableScrollWidth, setTableScrollWidth] = React.useState(0);

  React.useEffect(() => {
    const tableEl = tableScrollRef.current;
    if (!tableEl) return;
    // The wrapper itself is the scroll container (`overflow: auto` +
    // bounded height below), so start the search at it. Fall back to an
    // ancestor / `<main>` defensively if a variant ever removes that.
    let n: HTMLElement | null = tableEl;
    while (n) {
      const ox = getComputedStyle(n).overflowX;
      if (ox === "auto" || ox === "scroll") break;
      if (n.tagName === "MAIN") break;
      n = n.parentElement;
    }
    scrollAncestorRef.current = n;

    const update = () => {
      const ancestor = scrollAncestorRef.current;
      if (!ancestor) {
        setTableScrollWidth(0);
        return;
      }
      // Show the top scrollbar only when the ancestor actually overflows
      // horizontally — otherwise an empty scrollbar strip would float
      // above narrow tables.
      setTableScrollWidth(
        ancestor.scrollWidth > ancestor.clientWidth ? ancestor.scrollWidth : 0,
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(tableEl);
    if (scrollAncestorRef.current) ro.observe(scrollAncestorRef.current);

    // Mirror ancestor → top scrollbar so right-side native scrolling
    // and the mirror stay in sync regardless of which one the user
    // grabs.
    const ancestor = scrollAncestorRef.current;
    const onAncestorScroll = () => {
      if (topScrollRef.current && ancestor) {
        topScrollRef.current.scrollLeft = ancestor.scrollLeft;
      }
    };
    ancestor?.addEventListener("scroll", onAncestorScroll, { passive: true });

    return () => {
      ro.disconnect();
      ancestor?.removeEventListener("scroll", onAncestorScroll);
    };
  }, [rows.length, columns.length]);

  const onTopScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const ancestor = scrollAncestorRef.current;
    if (ancestor) {
      ancestor.scrollLeft = e.currentTarget.scrollLeft;
    }
  };
  // Kept on `tableScrollRef` for backward-compatibility — the inner
  // wrapper isn't a scroll container today, so this is a no-op unless a
  // future variant restores an inner scroll container.
  const onTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  // ── Frozen-header scroll sync ─────────────────────────────────────────────
  // The header and body are separate horizontal scrollers (so the header can
  // be `position: sticky` against the page while the body scrolls). Keep their
  // `scrollLeft` in lockstep; the guard prevents the scroll→scroll feedback
  // loop between the two.
  const frozenHeaderRef = React.useRef<HTMLDivElement>(null);
  const frozenBodyRef = React.useRef<HTMLDivElement>(null);
  const syncingRef = React.useRef(false);
  const syncScroll = (from: HTMLElement | null, to: HTMLElement | null) => {
    if (!from || !to || syncingRef.current) return;
    syncingRef.current = true;
    to.scrollLeft = from.scrollLeft;
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  };
  // Visible leaf columns drive the shared `<colgroup>` so both tables resolve
  // identical per-column widths under `table-layout: fixed`.
  const leafColumns = table.getVisibleLeafColumns();
  const colgroup = (
    <colgroup>
      {leafColumns.map((col) => (
        <col key={col.id} style={{ width: col.getSize() }} />
      ))}
    </colgroup>
  );

  // Header + body cells, extracted so the normal single-table path and the
  // frozen-header two-table path render identical cells. In the frozen path
  // column widths come from the shared `<colgroup>` (table-layout: fixed), so
  // the per-`th` inline width is dropped there.
  const headerRowsEl = table.getHeaderGroups().map((headerGroup) => (
    <tr key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        const canSort = header.column.getCanSort();
        const sortState = header.column.getIsSorted();
        const isActions = header.column.id === "actions";
        return (
          <th
            key={header.id}
            className={cn(
              "relative sticky top-0 z-[2] bg-card border-b border-border px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-text-dim",
              canSort && "cursor-pointer select-none hover:text-foreground",
              sortState && "text-foreground",
              isActions && "right-0 z-[3]",
            )}
            style={
              frozenHeader
                ? undefined
                : resizable
                  ? { width: header.getSize() }
                  : { width: header.getSize() !== 150 ? header.getSize() : undefined }
            }
          >
            {/* The sort handler lives on the inner span, not the `th`: a
                mousedown on the resize grip would otherwise bubble up and
                toggle the sort when the drag ends. */}
            <span
              onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
              className="inline-flex items-center gap-1.5"
            >
              {header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext())}
              {canSort && (
                <span className="text-text-dim">
                  {sortState === "asc" ? (
                    <ChevronUp size={14} className="text-foreground" />
                  ) : sortState === "desc" ? (
                    <ChevronDown size={14} className="text-foreground" />
                  ) : (
                    <ChevronsUpDownTiny />
                  )}
                </span>
              )}
            </span>
            {resizable && header.column.getCanResize() && (
              <span
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize column"
                onDoubleClick={() => header.column.resetSize()}
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  "absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none select-none",
                  "hover:bg-primary-fill",
                  header.column.getIsResizing() && "bg-primary",
                )}
              />
            )}
          </th>
        );
      })}
    </tr>
  ));

  const bodyRowsEl = isLoading ? (
    Array.from({ length: loadingRows }).map((_, i) => (
      <tr key={`skeleton-${i}`} className="border-b border-border last:border-b-0">
        {columns.map((_col, j) => (
          <td key={j} className="px-4 py-3">
            <Skeleton className="h-4 w-full max-w-[180px]" />
          </td>
        ))}
      </tr>
    ))
  ) : rows.length === 0 ? (
    <tr>
      <td colSpan={leafColumns.length} className="px-4">
        {typeof emptyMessage === "string" ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          emptyMessage
        )}
      </td>
    </tr>
  ) : (
    rows.map((row) => (
      <tr
        key={row.id}
        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        className={cn(
          "group/row border-b border-border last:border-b-0 transition-colors",
          onRowClick && "cursor-pointer hover:bg-row-hover",
        )}
      >
        {row.getVisibleCells().map((cell) => {
          const isActions = cell.column.id === "actions";
          return (
            <td
              key={cell.id}
              className={cn(
                "px-4 py-2.5 text-foreground",
                isActions &&
                  "sticky right-0 z-[1] bg-card shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.12)] before:absolute before:bottom-0 before:left-0 before:top-0 before:w-px before:bg-border",
              )}
            >
              {renderCell(cell, onCellEdit)}
            </td>
          );
        })}
      </tr>
    ))
  );

  // Shared fixed-layout style for the frozen-header tables. Both header and
  // body MUST resolve to the same width or the columns drift apart — so pin an
  // explicit width = the summed column sizes (NOT `max-content`, which sizes
  // each table to its own content: short header labels vs long body values).
  // `min-width: 100%` lets it fill a wider container; fixed layout then
  // distributes the extra identically across both tables.
  const totalColumnWidth = leafColumns.reduce((sum, col) => sum + col.getSize(), 0);
  const frozenTableStyle: React.CSSProperties = {
    tableLayout: "fixed",
    width: totalColumnWidth,
    minWidth: "100%",
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {(searchPlaceholder || toolbarActions) && (
        <div className="flex flex-wrap items-center gap-2">
          {searchPlaceholder && (
            <div className="relative min-w-0 flex-1 sm:max-w-sm">
              <Search
                size={12}
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-dim"
              />
              <Input
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-9 pl-6"
              />
            </div>
          )}
          {toolbarActions && (
            <div className="ml-auto flex items-center gap-2">{toolbarActions}</div>
          )}
        </div>
      )}

      {!disableMobileCards && (
        <MobileCardList
          table={table}
          isLoading={isLoading}
          loadingRows={loadingRows}
          emptyMessage={emptyMessage}
          onRowClick={onRowClick}
          onCellEdit={onCellEdit}
        />
      )}

      {frozenHeader && (
        <div
          className={cn(
            // `overflow-clip` clips to the rounded corners WITHOUT becoming a
            // scroll container — so the sticky header below escapes to the page
            // instead of being captured by the card.
            "flex flex-col overflow-clip rounded-xl border border-border bg-card shadow-card",
            !disableMobileCards && "hidden sm:flex",
          )}
        >
          {/* Frozen header: a separate horizontal scroller that sticks to the
              PAGE (the clip card doesn't capture it). Its thin scrollbar is the
              always-visible horizontal pan control; `frozenHeaderTop` offsets it
              below any fixed chrome (e.g. a sticky topbar). */}
          <div
            ref={frozenHeaderRef}
            onScroll={(e) => syncScroll(e.currentTarget, frozenBodyRef.current)}
            className="scrollbar-thin sticky z-[4] overflow-x-auto overflow-y-hidden bg-card"
            style={{ top: frozenHeaderTop }}
          >
            <table className="w-full text-sm" style={frozenTableStyle}>
              {colgroup}
              <thead className="text-text-dim">{headerRowsEl}</thead>
            </table>
          </div>
          {/* Body: scrolls horizontally (synced to the header) and grows with
              the page vertically. With `keepColumnWidths` it shows its own
              horizontal scrollbar at the bottom of the grid, so the wide grid
              has the top and bottom scroll bars that prop promises; otherwise
              the header's bar is the only one. */}
          <div
            ref={frozenBodyRef}
            onScroll={(e) => syncScroll(e.currentTarget, frozenHeaderRef.current)}
            className={cn(
              "overflow-x-auto",
              keepColumnWidths ? "scrollbar-thin" : "scrollbar-none",
            )}
          >
            <table className="w-full text-sm" style={frozenTableStyle}>
              {colgroup}
              <tbody>{bodyRowsEl}</tbody>
            </table>
          </div>
        </div>
      )}

      {!frozenHeader && (
      <div
        className={cn(
          "flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card",
          // Phones get the stacked card list above instead; the classic table
          // returns at `sm` (tablets and up). When mobile cards are disabled,
          // the table shows at every width.
          !disableMobileCards && "hidden sm:flex",
        )}
      >
        {/* Top scrollbar — sits flush above the table and mirrors the
            page-level horizontal scroll. Sticky to the top of `<main>`
            so a wide table can be panned without scrolling all the way
            down to the native bottom scrollbar. Hidden when the page
            doesn't actually overflow horizontally. `z-[3]` keeps it
            above the sticky `thead` (z-2) so vertical scroll doesn't
            visually overlap. */}
        {tableScrollWidth > 0 && (
          <div
            ref={topScrollRef}
            onScroll={onTopScroll}
            className="scrollbar-thin sticky top-0 z-[3] overflow-x-auto overflow-y-hidden bg-card"
            aria-hidden="true"
          >
            <div style={{ width: tableScrollWidth, height: 1 }} />
          </div>
        )}

        {/* Horizontal scroll for wide grids; vertical scroll stays at the
            page level (no scrollbar inside the grid — it behaves like every
            other table). */}
        <div
          ref={tableScrollRef}
          onScroll={onTableScroll}
          className="scrollbar-thin overflow-x-auto"
        >
          <table
            className="w-full text-sm"
            style={keepColumnWidths ? { minWidth: totalColumnWidth } : undefined}
          >
            <thead className="text-text-dim">{headerRowsEl}</thead>
            <tbody>{bodyRowsEl}</tbody>
          </table>
        </div>

      </div>
      )}

      {/* Footer lives OUTSIDE the table wrapper so it serves both the desktop
          table and the mobile card list (the wrapper is `hidden` on phones).
          Reads everything off the shared `table` instance, so pagination /
          result count / rows-per-page work identically in both layouts. */}
      {showFooter && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-surface-elevated px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <span>
              {formatNumber(resultCount)} result{resultCount === 1 ? "" : "s"}
            </span>
            {server ? (
              <label className="flex items-center gap-2">
                <span>Rows per page</span>
                <Select
                  value={String(server.perPage)}
                  onValueChange={(v) => server.onPerPageChange(Number(v))}
                >
                  <SelectTrigger className="h-7 w-[88px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(server.perPageOptions ?? DEFAULT_SERVER_PAGE_SIZES).map(
                      (opt) => (
                        <SelectItem key={opt} value={String(opt)}>
                          {opt}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </label>
            ) : (
              pageSizeOptions && (
                <label className="flex items-center gap-2">
                  <span>Rows per page</span>
                  <Select
                    value={String(pageSizeState)}
                    onValueChange={(v) => {
                      const next: PageSizeOption =
                        v === "all" ? "all" : Number(v);
                      setPageSizeState(next);
                      onPageSizeChange?.(next);
                    }}
                  >
                    <SelectTrigger className="h-7 w-[88px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {pageSizeOptions.map((opt) => (
                        <SelectItem key={String(opt)} value={String(opt)}>
                          {opt === "all" ? "All" : opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
              )
            )}
          </div>
          {showPagination && (
            <div className="flex items-center gap-2">
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={goPrev}
                disabled={!canPrev}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={goNext}
                disabled={!canNext}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Phone (<640px) layout: every row becomes a stacked label:value card. Reads
 * off the same TanStack `table` instance the desktop view uses, so sorting,
 * search, pagination, and the footer all stay in sync — only the per-row
 * presentation differs. Each visible cell renders via the same `flexRender`
 * the table uses; the column header is the label. Columns with an empty
 * header (the `actions` row menu, an avatar-only column) render unlabeled and
 * pin to the card's top-right corner.
 */
function MobileCardList<TData>({
  table,
  isLoading,
  loadingRows,
  emptyMessage,
  onRowClick,
  onCellEdit,
}: {
  table: Table<TData>;
  isLoading: boolean;
  loadingRows: number;
  emptyMessage: React.ReactNode;
  onRowClick?: (row: TData) => void;
  onCellEdit?: (row: TData, field: string, value: string | null) => Promise<void>;
}) {
  const rows = table.getRowModel().rows;

  // columnId → rendered header node, with the proper HeaderContext (so JSX
  // headers render correctly, not just string headers). Depends only on the
  // table instance — headers are stable across data/row changes; they only
  // shift when the column defs do. Shared by every card as the field labels.
  const headerLabels = React.useMemo(() => {
    const map = new Map<string, React.ReactNode>();
    for (const header of table.getFlatHeaders()) {
      if (header.isPlaceholder) continue;
      map.set(
        header.column.id,
        flexRender(header.column.columnDef.header, header.getContext()),
      );
    }
    return map;
  }, [table]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 sm:hidden">
        {Array.from({ length: loadingRows }).map((_, i) => (
          <div
            key={`m-skeleton-${i}`}
            className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-card"
          >
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-full max-w-[200px]" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card shadow-card sm:hidden">
        {typeof emptyMessage === "string" ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          emptyMessage
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 sm:hidden">
      {rows.map((row) => (
        <MobileCard
          key={row.id}
          row={row}
          headerLabels={headerLabels}
          onRowClick={onRowClick}
          onCellEdit={onCellEdit}
        />
      ))}
    </div>
  );
}

function MobileCard<TData>({
  row,
  headerLabels,
  onRowClick,
  onCellEdit,
}: {
  row: Row<TData>;
  headerLabels: Map<string, React.ReactNode>;
  onRowClick?: (row: TData) => void;
  onCellEdit?: (row: TData, field: string, value: string | null) => Promise<void>;
}) {
  const cells = row.getVisibleCells();
  // Split into labeled fields (header text present) and unlabeled "corner"
  // cells (actions menu, avatar-only column). Corner cells float top-right.
  const corner: Cell<TData, unknown>[] = [];
  const fields: Cell<TData, unknown>[] = [];
  for (const cell of cells) {
    if (hasHeaderLabel(cell.column.columnDef.header)) fields.push(cell);
    else corner.push(cell);
  }

  return (
    <div
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
      className={cn(
        "group/row relative flex flex-col gap-2.5 rounded-xl border border-border bg-card p-4 shadow-card transition-colors",
        onRowClick && "cursor-pointer hover:bg-row-hover",
      )}
    >
      {corner.length > 0 && (
        // Stop row-click bubbling so tapping the menu doesn't also fire the
        // card's navigate-on-click.
        <div
          className="absolute right-2 top-2 flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {corner.map((cell) => (
            <React.Fragment key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </React.Fragment>
          ))}
        </div>
      )}

      {fields.map((cell, i) => {
        // First labeled field reads as the card title: header hidden, value
        // larger. Pads its right side so a long title doesn't run under the
        // corner actions.
        const isTitle = i === 0;
        return (
          <div
            key={cell.id}
            className={cn(
              isTitle
                ? "flex flex-col pr-10"
                : "flex items-start justify-between gap-3",
            )}
          >
            {!isTitle && (
              <span className="shrink-0 pt-0.5 text-xs font-medium uppercase tracking-wider text-text-dim">
                {headerLabels.get(cell.column.id)}
              </span>
            )}
            <span
              className={cn(
                "min-w-0 text-foreground",
                isTitle
                  ? "text-base font-semibold"
                  : "text-right text-sm",
              )}
            >
              {renderCell(cell, onCellEdit)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** A column carries a label when its header is a non-empty string or any
 *  non-string node (JSX header). Empty string / null / undefined → unlabeled
 *  (renders in the card's top-right corner). */
function hasHeaderLabel(header: unknown): boolean {
  if (header == null) return false;
  if (typeof header === "string") return header.trim().length > 0;
  return true;
}

function ChevronsUpDownTiny() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M4 5.5 L7 3 L10 5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 8.5 L7 11 L10 8.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
